import { featureFlags } from '@shared-services/feature-flags';
import { logger } from '@shared-services/log-service';
import { getWindowInBrowser } from 'client/RuntimeWindow';
import useEditor from 'client/widget-components/customHooks/useEditor';
import { ProductEntity } from 'client/widget-components/ecom/stores/types';

const { isInPreviewOrEditor } = useEditor();

/**
 * After ecom ready, session (including `defaultCurrency`) is consistent for analytics payloads.
 */
export async function prepareAndSendItemViewEvents(
    product: ProductEntity
): Promise<void> {
    if (!window?.runtime || isInPreviewOrEditor()) {
        return;
    }
    if (!window._ecomm) {
        return;
    }

    try {
        await window._ecomm.ready;
    } catch (error) {
        logger.error({
            message: 'Error waiting for ecom ready before item view events',
            error,
        });
        return;
    }

    sendSafely('view item analytics', () =>
        sendGAEvent('view_item', product.price, getViewItemPayload(product))
    );
    sendSafely('Meta Pixel ViewContent', () =>
        sendMetaViewContentEvent(product)
    );
}

function sendSafely(eventLabel: string, send: () => void) {
    try {
        send();
    } catch (error) {
        logger.error({
            message: `Error sending ${eventLabel} event`,
            error,
        });
    }
}

function getCurrency() {
    return window._ecomm!.state.session.storeSettings.defaultCurrency.toUpperCase();
}

function getNameFromProduct(product: ProductEntity) {
    try {
        const variationChoices = product.selected_options_values
            ? JSON.parse(product.selected_options_values)
            : {};

        if (Object.keys(variationChoices).length == 0) return product.name;

        const spaceSeparatedSelectedChoices = `${Object.values(
            variationChoices
        ).join(' ')}`;
        return `${product.name} ${spaceSeparatedSelectedChoices}`;
    } catch (error) {
        logger.error({
            message: `Error getting name from product`,
            error,
        });
        return product.name;
    }
}

function getViewItemPayload(product: ProductEntity) {
    let name = getNameFromProduct(product);

    return {
        currency: getCurrency(),
        items: [
            {
                item_id: product.itemId, //GA4
                item_name: name, //GA4
                id: product.itemId, //UA
                name, //UA
                price: product.price,
                description: product.description,
            },
        ],
    };
}

function sendGAEvent(event: string, value: number, payload: any) {
    window?.runtime?.tagManagerAPI.sendGAEvent({
        category: event,
        action: event,
        value: value,
        siteAlias: getWindowInBrowser()?.Parameters?.SiteAlias,
        payload: payload,
    });
}

// A variation change sends the item view events again with the same product id.
// ViewContent counts product views, so it is sent one time per product.
const productIdsSentToMeta = new Set<string>();

// The cart's Meta Pixel events (MetaPixelProvider in snipcart-frontend) send the product id,
// not the variation id. ViewContent must send the same id so Meta can match the events.
function sendMetaViewContentEvent(product: ProductEntity) {
    if (
        !featureFlags.getBoolean('runtime.ssr.metaPixelViewContent.enabled') ||
        typeof window.fbq !== 'function' ||
        productIdsSentToMeta.has(product.productId)
    ) {
        return;
    }
    productIdsSentToMeta.add(product.productId);
    window.fbq('track', 'ViewContent', {
        content_ids: [product.productId],
        contents: [{ id: product.productId, quantity: 1 }],
        content_type: 'product',
        currency: getCurrency(),
        value: product.price,
    });
}
